/**
 * Change Windows Tab - Admin Console
 * Requirement 37.7: declare "we know a cost change is coming on this
 * service, don't alert on it" ahead of a planned deploy/migration - real
 * CRUD against optimization-api's change_windows table, consumed by
 * anomaly.service.ts's applyChangeWindowSuppression() before severity is
 * finalized on the next detection run.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getActiveTenantId } from '../../services/tenantContext';
import { useAuthStore } from '../../store/authStore';

const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';

function getHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return {
    'x-tenant-id': getActiveTenantId(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

interface ChangeWindow {
  id: string;
  service: string;
  provider: string | null;
  starts_at: string;
  ends_at: string;
  reason: string;
  created_by: string;
  created_at: string;
}

function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
}

export const ChangeWindowsTab: React.FC = () => {
  const { user } = useAuthStore();
  const [windows, setWindows] = useState<ChangeWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const now = new Date();
  const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);
  const [form, setForm] = useState({
    service: '',
    provider: '',
    starts_at: toLocalInputValue(now.toISOString()),
    ends_at: toLocalInputValue(inOneHour.toISOString()),
    reason: '',
  });

  const fetchWindows = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${OPTIMIZATION_API_URL}/api/v1/change-windows`, { headers: getHeaders() });
      setWindows(res.data?.data || []);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load change windows');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWindows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.service.trim() || !form.reason.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await axios.post(
        `${OPTIMIZATION_API_URL}/api/v1/change-windows`,
        {
          service: form.service.trim(),
          provider: form.provider || null,
          starts_at: new Date(form.starts_at).toISOString(),
          ends_at: new Date(form.ends_at).toISOString(),
          reason: form.reason.trim(),
          created_by: user?.email || 'unknown',
        },
        { headers: { ...getHeaders(), 'Content-Type': 'application/json' } }
      );
      setForm((prev) => ({ ...prev, service: '', reason: '' }));
      await fetchWindows();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Failed to create change window');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${OPTIMIZATION_API_URL}/api/v1/change-windows/${id}`, { headers: getHeaders() });
      setWindows((prev) => prev.filter((w) => w.id !== id));
    } catch (e: any) {
      setError(e.message || 'Failed to delete change window');
    }
  };

  const isActive = (w: ChangeWindow) => {
    const nowMs = Date.now();
    return new Date(w.starts_at).getTime() <= nowMs && new Date(w.ends_at).getTime() >= nowMs;
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="mb-1.5 text-sm font-semibold text-foreground">Mark Upcoming Deploy as Expected Cost Change</h3>
        <p className="mb-6 text-sm text-muted-foreground">
          Anomalies detected on the named service during an active window are downgraded to low severity instead of
          alerting, since the cost change is already expected.
        </p>

        {error && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
            {error}
          </div>
        )}

        <form onSubmit={handleCreate} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">Service name</label>
            <input
              type="text"
              value={form.service}
              onChange={(e) => setForm((prev) => ({ ...prev, service: e.target.value }))}
              placeholder="e.g. Amazon EC2"
              required
              className="w-full rounded-lg border border-border px-4 py-2 focus:border-ring focus:ring-2 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">Provider (optional - blank = any)</label>
            <select
              value={form.provider}
              onChange={(e) => setForm((prev) => ({ ...prev, provider: e.target.value }))}
              className="w-full rounded-lg border border-border px-4 py-2 focus:border-ring focus:ring-2 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
            >
              <option value="">Any provider</option>
              <option value="aws">AWS</option>
              <option value="azure">Azure</option>
              <option value="gcp">GCP</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">Starts</label>
            <input
              type="datetime-local"
              value={form.starts_at}
              onChange={(e) => setForm((prev) => ({ ...prev, starts_at: e.target.value }))}
              required
              className="w-full rounded-lg border border-border px-4 py-2 focus:border-ring focus:ring-2 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">Ends</label>
            <input
              type="datetime-local"
              value={form.ends_at}
              onChange={(e) => setForm((prev) => ({ ...prev, ends_at: e.target.value }))}
              required
              className="w-full rounded-lg border border-border px-4 py-2 focus:border-ring focus:ring-2 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-foreground/80">Reason</label>
            <input
              type="text"
              value={form.reason}
              onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="e.g. Planned autoscaling load test, 2026-08-15"
              required
              className="w-full rounded-lg border border-border px-4 py-2 focus:border-ring focus:ring-2 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={creating}
              className="rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Change Window'}
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="mb-4 text-sm font-semibold text-foreground">Change Windows</h3>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : windows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No change windows declared.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Service</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Provider</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Window</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Reason</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border dark:divide-border">
                {windows.map((w) => (
                  <tr key={w.id}>
                    <td className="px-3 py-2 text-sm text-foreground">{w.service}</td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">{w.provider || 'Any'}</td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">
                      {new Date(w.starts_at).toLocaleString()} → {new Date(w.ends_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">{w.reason}</td>
                    <td className="px-3 py-2 text-sm">
                      {isActive(w) ? (
                        <span className="rounded bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Active</span>
                      ) : (
                        <span className="rounded bg-secondary px-2 py-1 text-xs font-medium text-muted-foreground dark:bg-secondary dark:text-muted-foreground/70">Inactive</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => handleDelete(w.id)} className="text-xs font-medium text-rose-600 hover:text-rose-800">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
